
import { defineComponent, PropType, watch, nextTick } from "vue";
import { CurrencyDisplay, useCurrencyInput } from "vue-currency-input";
import type { CurrencyInputOptions } from "vue-currency-input";
import { NUMBER_FORMAT_LOCALE } from "@/core/helpers/number-format";

const buildCurrencyInputOptions = (
  min: number | undefined,
  max: number
): CurrencyInputOptions => ({
  autoDecimalDigits: false,
  currency: "USD",
  currencyDisplay: CurrencyDisplay.hidden,
  locale: NUMBER_FORMAT_LOCALE,
  precision: { min: 0, max: 2 },
  useGrouping: true,
  hideGroupingSeparatorOnFocus: false,
  hideCurrencySymbolOnFocus: true,
  hideNegligibleDecimalDigitsOnFocus: true,
  valueRange: {
    ...(min !== undefined ? { min } : {}),
    max,
  },
});

export default defineComponent({
  name: "QuantityInputCustom",

  props: {
    classStr: {
      type: String,
    },
    inputType: {
      type: String,
      default: "text",
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Number as PropType<number | undefined>,
      default: undefined,
    },
    max: {
      type: Number as PropType<number>,
      default: Number.POSITIVE_INFINITY,
    },
    modelValue: Number,
    name: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
  },

  emits: ["input", "update:modelValue", "change", "blur", "focus"],

  setup(props, { emit }) {
    const { inputRef, formattedValue, numberValue, setValue, setOptions } =
      useCurrencyInput(buildCurrencyInputOptions(props.min, props.max));

    watch(
      () => [props.min, props.max] as const,
      ([min, max]) => {
        setOptions(buildCurrencyInputOptions(min, max));

        if (numberValue.value != null) {
          setValue(numberValue.value);
        }
      }
    );

    watch(
      () => props.modelValue,
      (value) => {
        if (value !== numberValue.value) {
          setValue(value ?? null);
        }
      },
      { immediate: true }
    );

    watch(numberValue, (value) => {
      emit("update:modelValue", value);
    });

    const getNumericValue = () => numberValue.value;

    const handleInput = async () => {
      await nextTick();
      emit("input", getNumericValue());
    };

    const handleChange = () => {
      emit("change", getNumericValue());
    };

    const handleBlur = () => {
      emit("blur", getNumericValue());
    };

    const handleFocus = (event: FocusEvent) => {
      nextTick(() => {
        const target = event.target as HTMLInputElement | null;
        target?.select();
      });
      emit("focus", event);
    };

    const focus = () => {
      const input = inputRef.value as { focus?: () => void } | null;
      input?.focus?.();
    };

    return {
      formattedValue,
      inputRef,
      focus,
      handleInput,
      handleChange,
      handleBlur,
      handleFocus,
    };
  },
});
